import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo
} from 'react';
import './monitoring.css';
import {
  Box,
  Typography,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  Paper,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { getMonitoring } from '../../api/axios';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import GarageIcon from '@mui/icons-material/Garage';
import { useTranslation } from 'react-i18next';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';

const Monitoring = () => {
  const { t } = useTranslation('global');

  const eventsRef = useRef([]);
  const isFirstLoad = useRef(true);
  const isFetchingRef = useRef(false);

  const [events, setEvents] = useState([]);
  const [timestamp, setTimestamp] = useState(Date.now());
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [barriers, setBarriers] = useState([]);
  const [selectedBarrier, setSelectedBarrier] = useState('');
  const rowsPerPage = 20; // broj redova po stranici

  const filteredEvents = useMemo(() => {
    if (!Array.isArray(events)) return [];
    let result = events;
    if (selectedBarrier)
      result = result.filter(e => (e.reader || '') === selectedBarrier);
    if (searchTerm)
      result = result.filter(e =>
        Object.values(e).some(
          val =>
            val != null &&
            val.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    return result;
  }, [events, selectedBarrier, searchTerm]);

  const currentEvents = filteredEvents.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  function formatToDateOrKeepOriginal(input) {
    const parsedDate = new Date(input);

    // Check if the parsed date is valid
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate.toISOString().split('T')[0]; // Extract YYYY-MM-DD
    } else {
      return input; // Return original string if not a valid date
    }
  }

  const audioRef = useRef(null);
  // init audio once
  useEffect(() => {
    audioRef.current = new Audio('/notification.mp3');
  }, []);

  const fetchData = useCallback(async () => {
    // Prevent overlapping fetches
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    try {
      const response = await getMonitoring();

      const newFetchedEvents = Array.isArray(response)
        ? response.slice(0, 100)
        : [];

      const newEntries = newFetchedEvents.filter(
        newEvent =>
          !eventsRef.current.some(
            existingEvent =>
              existingEvent.serial_num === newEvent.serial_num &&
              existingEvent.datetime === newEvent.datetime
          )
      );

      if (!isFirstLoad.current && newEntries.length > 0 && audioRef.current) {
        audioRef.current.play().catch(() => {});
      }

      isFirstLoad.current = false;

      setEvents(newFetchedEvents);
      eventsRef.current = newFetchedEvents;
      setTimestamp(Date.now());
    } catch (error) {
      console.error(t('monitoring.errorFetchingEvents'), error);
    } finally {
      isFetchingRef.current = false;
    }
  }, [t]);

  useEffect(() => {
    fetchData();

    const interval = setInterval(() => {
      fetchData();
    }, 1000);

    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    const uniqueBarriers = [...new Set(events.map(e => e.reader).filter(Boolean))];
    setBarriers(uniqueBarriers);
  }, [events]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedBarrier, searchTerm]);

  return (
    <Box sx={{ padding: 3, margin: '0 auto' }}>
      <Box sx={{ padding: 3, maxWidth: 1200, margin: '0 auto' }}>
        <Typography variant="h4" gutterBottom>
          {t('monitoring.monitor')}
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: 2 }}>
          {t('monitoring.lastUpdate')}{' '}
          {new Date(timestamp).toLocaleTimeString()}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
            marginBottom: 3
          }}
        >
          <TextField
            label={t('monitoring.search')}
            variant="outlined"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            size="small"
            sx={{
              width: { xs: '100%', sm: 300 },
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white', // bela pozadina za ceo input
                borderRadius: 2, // zaobljeni uglovi za input
                overflow: 'hidden', // uklanja “visak” u uglovima
                '& fieldset': {
                  borderRadius: 2 // outline border radius da se poklapa
                },
                '& input': {
                  backgroundColor: 'white' // input unutra takođe bela pozadina
                }
              },
              '& .MuiInputLabel-root': {
                fontSize: 15
              },
              '& .MuiOutlinedInput-root.Mui-focused': {
                backgroundColor: 'white' // bela pozadina i na fokus
              }
            }}
          />
          <FormControl
            size="small"
            sx={{
              minWidth: 225,
              '& .MuiInputLabel-root, & .MuiSelect-select': { fontSize: 15 }
            }}
          >
            <InputLabel id="monitoring-barrier-label">
              {t('monitoring.barrier')}
            </InputLabel>
            <Select
              labelId="monitoring-barrier-label"
              value={selectedBarrier}
              label={t('monitoring.barrier')}
              onChange={e => setSelectedBarrier(e.target.value)}
              sx={{ backgroundColor: 'white', borderRadius: 2 }}
              MenuProps={{
                PaperProps: {
                  sx: { '& .MuiMenuItem-root': { fontSize: 15 } }
                }
              }}
            >
              <MenuItem value="">{t('monitoring.allBarriers')}</MenuItem>
              {barriers.map(b => (
                <MenuItem key={b} value={b}>
                  {b}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      {filteredEvents.length > 0 ? (
        <Paper elevation={3} sx={{ overflow: 'auto', zoom: '0.9' }}>
          <Table sx={{ tableLayout: 'auto' }}>
            <TableHead>
              <TableRow>
                <TableCell className="monitoring-padding">
                  <Tooltip title={t('monitoring.fullPassType')} arrow>
                    <span>{t('monitoring.pass_type')}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  className="monitoring-padding"
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  <Tooltip title={t('monitoring.ticket')} arrow>
                    <span>{t('monitoring.ticket')}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  className="monitoring-padding"
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  <Tooltip title={t('monitoring.registration')} arrow>
                    <span>{t('monitoring.registration')}</span>
                  </Tooltip>
                </TableCell>
                <TableCell className="monitoring-padding">
                  <Tooltip title={t('monitoring.dateAndTime')} arrow>
                    <span>{t('monitoring.dateAndTime')}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  className="monitoring-padding"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <Tooltip title={t('monitoring.barrier')} arrow>
                    <span>{t('monitoring.barrier')}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  className="monitoring-padding"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <Tooltip title={t('monitoring.parkingColumn')} arrow>
                    <span>{t('monitoring.parkingColumn')}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  className="monitoring-padding"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <Tooltip title={t('monitoring.accessType')} arrow>
                    <span>{t('monitoring.accessType')}</span>
                  </Tooltip>
                </TableCell>
                <TableCell className="monitoring-padding">
                  <Tooltip title={t('monitoring.result')} arrow>
                    <span>{t('monitoring.result')}</span>
                  </Tooltip>
                </TableCell>
                <TableCell className="monitoring-padding">
                  <Tooltip title={t('monitoring.eventType')} arrow>
                    <span>{t('monitoring.eventType')}</span>
                  </Tooltip>
                </TableCell>
                <TableCell className="monitoring-padding">
                  <Tooltip title={t('monitoring.name')} arrow>
                    <span>{t('monitoring.name')}</span>
                  </Tooltip>
                </TableCell>
                <TableCell className="monitoring-padding">
                  <Tooltip title={t('monitoring.lastName')} arrow>
                    <span>{t('monitoring.lastName')}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  className="monitoring-padding"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <Tooltip title={t('monitoring.fullPassesRemaining')} arrow>
                    <span>{t('monitoring.passesRemaining')}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  className="monitoring-padding"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <Tooltip title={t('monitoring.fullDaysRemaining')} arrow>
                    <span>{t('monitoring.daysRemaining')}</span>
                  </Tooltip>
                </TableCell>
                <TableCell className="monitoring-padding">
                  <Tooltip title={t('monitoring.fullRemainingToday')} arrow>
                    <span>{t('monitoring.remainingToday')}</span>
                  </Tooltip>
                </TableCell>
                <TableCell className="monitoring-padding">
                  <Tooltip title={t('monitoring.fullExpireDate')} arrow>
                    <span>{t('monitoring.expireDate')}</span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentEvents.map((event, index) => (
                <TableRow
                  key={`${event.serial_num}-${event.datetime}`}
                  sx={{
                    backgroundColor:
                      event.reject === 'Success'
                        ? 'rgba(76, 175, 80, 0.2)'
                        : 'rgba(244, 67, 54, 0.2)'
                  }}
                >
                  <TableCell
                    className="monitoring-padding"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Tooltip title={event.pass_type} arrow>
                      <span>
                        {event.pass_type === 'QR' ? (
                          <QrCodeIcon></QrCodeIcon>
                        ) : event.pass_type === 'MIFARE' ? (
                          <CreditCardIcon></CreditCardIcon>
                        ) : event.pass_type === 'PLATES' ? (
                          <GarageIcon></GarageIcon>
                        ) : event.pass_type === 'BUTTON' ? (
                          <RadioButtonCheckedIcon></RadioButtonCheckedIcon>
                        ) : (
                          event.pass_type
                        )}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    className="monitoring-padding"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Tooltip title={event.serial_num} arrow>
                      <span>{event.serial_num}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    className="monitoring-padding"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Tooltip title={event.registration} arrow>
                      <span>{event.registration}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    className="monitoring-padding"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Tooltip title={event.datetime} arrow>
                      <span>{event.time}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    className="monitoring-padding"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Tooltip title={event.reader} arrow>
                      <span>{event.reader}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    className="monitoring-padding"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Tooltip title={event.reader_group || '-'} arrow>
                      <span>{event.reader_group || '-'}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    className="monitoring-padding"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Tooltip title={event.access_type} arrow>
                      <span>{event.access_type}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    className="monitoring-padding"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Tooltip title={event.message} arrow>
                      <span>{event.message}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    className="monitoring-padding"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Tooltip title={event.event_type || '-'} arrow>
                      <span>{event.event_type || '-'}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    className="monitoring-padding"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Tooltip title={event.name} arrow>
                      <span>{event.name}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    className="monitoring-padding"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Tooltip title={event.surname} arrow>
                      <span>{event.surname}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    className="monitoring-padding"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Tooltip title={event.remaining_passes} arrow>
                      <span>{event.remaining_passes}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    className="monitoring-padding"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Tooltip title={event.remaining_days} arrow>
                      <span>{event.remaining_days}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    className="monitoring-padding"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Tooltip title={event.remaining_daily} arrow>
                      <span>{event.remaining_daily}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    className="monitoring-padding"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Tooltip title={event.exceed_date} arrow>
                      <span>
                        {formatToDateOrKeepOriginal(event.exceed_date)}
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: 2,
              marginBottom: 2
            }}
          >
            <Pagination
              count={Math.ceil(filteredEvents.length / rowsPerPage)}
              page={currentPage}
              onChange={(event, page) => setCurrentPage(page)}
              color="primary"
            />
          </Box>
        </Paper>
      ) : (
        <Typography variant="body1">{t('monitoring.empty')}</Typography>
      )}
    </Box>
  );
};

export default Monitoring;
